import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Card } from "@/components/ui/card";

const activitySchema = z.object({
  activity_type: z.string().min(1, "Activity type is required"),
  subject: z.string().min(1, "Subject is required").max(200),
  description: z.string().max(1000).optional(),
  activity_date: z.string().min(1, "Date is required"),
});

type ActivityFormData = z.infer<typeof activitySchema>;

interface Activity {
  id: string;
  activity_type: string;
  subject: string;
  description: string | null;
  activity_date: string;
  created_at: string;
}

interface ActivityDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  contact: any | null;
}

export function ActivityDialog({
  open,
  onOpenChange,
  contact,
}: ActivityDialogProps) {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [activities, setActivities] = useState<Activity[]>([]);

  const form = useForm<ActivityFormData>({
    resolver: zodResolver(activitySchema),
    defaultValues: {
      activity_type: "call",
      subject: "",
      description: "",
      activity_date: new Date().toISOString().split("T")[0],
    },
  });

  useEffect(() => {
    if (contact && open) {
      fetchActivities();
    }
  }, [contact, open]);

  const fetchActivities = async () => {
    if (!contact) return;

    try {
      const { data, error } = await supabase
        .from("activities")
        .select("*")
        .eq("contact_id", contact.id)
        .order("activity_date", { ascending: false });

      if (error) throw error;
      setActivities(data || []);
    } catch (error) {
      console.error("Error fetching activities:", error);
    }
  };

  const onSubmit = async (data: ActivityFormData) => {
    if (!contact) return;

    setLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const { error } = await supabase.from("activities").insert([{
        activity_type: data.activity_type,
        subject: data.subject,
        description: data.description || null,
        activity_date: data.activity_date,
        user_id: user.id,
        contact_id: contact.id,
      }]);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Activity added successfully",
      });

      form.reset({
        activity_type: "call",
        subject: "",
        description: "",
        activity_date: new Date().toISOString().split("T")[0],
      });

      fetchActivities();
    } catch (error) {
      console.error("Error saving activity:", error);
      toast({
        title: "Error",
        description: "Failed to save activity",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            Activities for {contact?.name || "Contact"}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <FormField
                control={form.control}
                name="activity_type"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Activity Type *</FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select activity type" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="call">Call</SelectItem>
                        <SelectItem value="email">Email</SelectItem>
                        <SelectItem value="meeting">Meeting</SelectItem>
                        <SelectItem value="note">Note</SelectItem>
                        <SelectItem value="task">Task</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="subject"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Subject *</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="Brief description" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Description</FormLabel>
                    <FormControl>
                      <Textarea
                        {...field}
                        placeholder="Detailed notes..."
                        rows={3}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="activity_date"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Date *</FormLabel>
                    <FormControl>
                      <Input {...field} type="date" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <Button type="submit" disabled={loading} className="w-full">
                {loading ? "Adding..." : "Add Activity"}
              </Button>
            </form>
          </Form>

          <div className="space-y-4">
            <h3 className="font-semibold">Recent Activities</h3>
            {activities.length === 0 ? (
              <p className="text-muted-foreground text-sm">
                No activities recorded yet
              </p>
            ) : (
              activities.map((activity) => (
                <Card key={activity.id} className="p-4">
                  <div className="flex justify-between items-start mb-2">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold capitalize">
                        {activity.activity_type}
                      </span>
                      <span className="text-sm text-muted-foreground">
                        {new Date(activity.activity_date).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                  <p className="font-medium mb-1">{activity.subject}</p>
                  {activity.description && (
                    <p className="text-sm text-muted-foreground">
                      {activity.description}
                    </p>
                  )}
                </Card>
              ))
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
